import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { ScreenInfo } from "src/app/settings/models/screen-info";
import {
  NaturalFormConverterResult,
  SectionForPresetInForm,
} from "src/app/shared/services/dynamic-form/dynamic-form.service";
import { environment } from "src/environments/environment";

import { User } from "../../../session/models/user";
import { HttpService } from "../../../session/services/http.service";
import { Inspection } from "../../model/inspection";
import { SOWFieldChanges } from "../../model/sow-field-change";
import { ManageDependenciesService } from "../manage-dependencies-service/manage-dependencies.service";

@Injectable({
  providedIn: "root",
})
export class InspectionFormService {
  public constructor(
    private httpService: HttpService,
    private manageDependenciesService: ManageDependenciesService,
  ) {}

  public getAllFormsForUserAccount(
    inspectionId: string | null = null,
  ): Observable<ScreenInfo[]> {
    return this.httpService
      .get(
        `${environment.webApiUrl}/api/form/account?inspectionId=${inspectionId ?? ""}`,
        {
          withCredentials: true,
        },
      )
      .pipe(
        map((screens: any[]) => {
          return screens;
        }),
      );
  }

  public async getDynamicFormById(
    formId: string,
  ): Promise<NaturalFormConverterResult> {
    const naturalForm: NaturalFormConverterResult = await this.httpService
      .get(`${environment.webApiUrl}/api/form/${formId}`, {
        withCredentials: true,
      })
      .toPromise();

    this.manageDependenciesService.translateFormDependenciesToBoolean(
      naturalForm.controls,
    );
    return naturalForm;
  }

  public async getSectionNamesByFormId(
    formId: string,
  ): Promise<SectionForPresetInForm[]> {
    const sectionNames: SectionForPresetInForm[] = await this.httpService
      .get(`${environment.webApiUrl}/api/form/${formId}/onlySectionNames`, {
        withCredentials: true,
      })
      .toPromise();
    return sectionNames;
  }

  public getAllInspectors(): Observable<User[]> {
    return this.httpService
      .get(`${environment.webApiUrl}/api/users/inspectors`, {
        withCredentials: true,
      })
      .pipe(
        map((users) => {
          return users.map((user) => {
            return new User(user);
          });
        }),
      );
  }

  public getInspection(id: string): Observable<Inspection> {
    return this.httpService
      .get(`${environment.webApiUrl}/api/inspection/${id}`, {
        withCredentials: true,
      })
      .pipe(
        map((inspection) => {
          return <Inspection>inspection;
        }),
      );
  }

  public async getInspectionId(externalInspectionId: string) {
    const result = <{ id: string }>(
      await this.httpService
        .get(
          `${environment.webApiUrl}/api/inspection/id/${externalInspectionId}`,
          { withCredentials: true },
        )
        .toPromise()
    );
    return result.id;
  }

  public async send(inspection: Inspection): Promise<void> {
    return await this.httpService
      .post(`${environment.webApiUrl}/api/inspection`, inspection, {
        withCredentials: true,
      })
      .toPromise();
  }

  public async update(inspection: Inspection): Promise<Inspection> {
    const response = await this.httpService
      .put(
        `${environment.webApiUrl}/api/inspection/${inspection.id}`,
        inspection,
        { withCredentials: true },
      )
      .toPromise();
    return response as Inspection;
  }

  public onScopeOfWorkFieldChange(scopeOfWorkFieldChangeList: SOWFieldChanges) {
    return this.httpService
      .put(
        `${environment.webApiUrl}/api/inspection/UpdateInspectionFormWeb`,
        scopeOfWorkFieldChangeList,
        { withCredentials: true },
      )
      .pipe(
        map((result) => {
          return <boolean>result;
        }),
      );
  }

  public async sendFullPictureToNetwork(
    inspectionId: string,
    itemId: string,
    fieldId: string,
    photo: string,
  ): Promise<void> {
    const params = {
      inspectionId,
      itemId,
      fieldId,
      photo,
    };

    if (photo && params.inspectionId != null) {
      const blob = this.convertBase64ToBlob(params.photo, "image/jpeg");

      const formData = new FormData();
      formData.append("file", blob, params.fieldId);
      formData.append("inspectionId", params.inspectionId);
      formData.append("itemId", params.itemId);
      formData.append("fieldId", params.fieldId);

      return await this.httpService.postImageFile(
        `${environment.webApiUrl}/api/pictures`,
        formData,
        { withCredentials: true },
      );
    }

    return await this.httpService
      .delete(
        `${environment.webApiUrl}/api/pictures?inspectionId=${inspectionId}&itemId=${itemId}&fieldId=${fieldId}`,
        { withCredentials: true },
      )
      .toPromise();
  }

  public onFullPictureRequested(
    inspectionId: string,
    productId: string,
    fieldId: string,
  ): Observable<string> {
    return this.httpService
      .get(
        `${environment.webApiUrl}/api/pictures?inspectionId=${inspectionId}&itemId=${productId}&fieldId=${fieldId}`,
        { withCredentials: true },
      )
      .pipe(
        map((result) => {
          return <string>result;
        }),
      );
  }

  public checkIfInspectionExists(inspectionName: string): Observable<boolean> {
    return this.httpService
      .get(
        `${environment.webApiUrl}/api/inspection/exists?inspectionName=${inspectionName}`,
        { withCredentials: true },
      )
      .pipe(
        map((result) => {
          return <boolean>result;
        }),
      );
  }

  public manageGroupSectionSelectionStatus(
    selectableSection: SectionForPresetInForm,
    sections: SectionForPresetInForm[],
  ): void {
    const selectedStatus: boolean = selectableSection["selected"];
    if (selectableSection.containsGroup) {
      sections
        .filter((section) => section.parentId === selectableSection.sectionId)
        .map((section) => (section["selected"] = selectedStatus));
    }
    if (selectableSection.parentId) {
      if (selectedStatus) {
        sections.find(
          (section) => section.sectionId === selectableSection.parentId,
        )["selected"] = selectedStatus;
      }
    }
  }

  private convertBase64ToBlob(base64File: string, type: any): Blob {
    const byteString = atob(base64File);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);

    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }

    return new Blob([ab], { type });
  }
}
